/**
 * Beispieldaten aus den Schemas — der Mock-Modus (`VITE_MOCK_API=1`).
 *
 * Warum es ihn gibt: In Nacht 2 antwortet `buchhaltung-api` auf jeden Endpunkt
 * mit `501` (die Router sind der Vertrag, Bahn C und D fuellen sie parallel).
 * Ohne Mock liesse sich keine einzige Seite ansehen, geschweige denn
 * durchklicken — und eine Oberflaeche, die nie jemand gesehen hat, ist keine
 * Oberflaeche, sondern eine Behauptung.
 *
 * Die Daten sind **nicht** ausgedacht, sondern aus den Schemas abgeleitet:
 * jedes Feld, das `DocumentResponse` kennt, kommt hier mindestens einmal
 * gefuellt vor, und die sechs Belege decken zusammen alles ab, was die Seiten
 * unterscheiden muessen — gruen, gelb, rot; PDF, Bild, XML; Mahnung, Dublette,
 * „sonstiges".
 *
 * Der Mock schreibt in ein Modul-Objekt und ist deshalb innerhalb einer
 * Sitzung zustandsbehaftet: Wer ablehnt, sieht den Beleg danach als abgelehnt.
 * Beim Neuladen ist alles wieder wie am Anfang — genau richtig fuer eine
 * Klickprobe, unbrauchbar als Datenbank, und das soll man auch merken.
 */

import type {
  Ablehnung,
  BankAccountResponse,
  BankListe,
  BankTransactionResponse,
  Belegart,
  Bestaetigung,
  BuchhaltungEinstellungen,
  DocumentListItem,
  DocumentListe,
  DocumentPatch,
  DocumentResponse,
  Freigabe,
  IbanBestaetigung,
  IngestHealth,
  LogEintrag,
  ReviewEntscheidung,
  ReviewItemResponse,
  ReviewListe,
  SeedImport,
  SupplierCreate,
  SupplierListe,
  SupplierResponse,
  SupplierUpdate,
  Uebergabeliste,
  UploadErgebnis,
  Zahlung,
  ZuZahlenListe,
  ZuordnungBestaetigen,
} from '@/types';

/** Ein fester „heute", damit Screenshots von gestern nicht anders aussehen. */
const HEUTE = new Date();

function tage(versatz: number): string {
  const d = new Date(HEUTE);
  d.setDate(d.getDate() + versatz);
  return d.toISOString().slice(0, 10);
}

function stunden(versatz: number): string {
  const d = new Date(HEUTE);
  d.setHours(d.getHours() + versatz);
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Belege
// ---------------------------------------------------------------------------

/** Was jeder Beleg hat und fast nie abweicht — spart sechsmal dieselbe Zeile. */
const GRUNDGERUEST: Pick<
  DocumentResponse,
  | 'currency'
  | 'hat_rohmail'
  | 'ist_auslage'
  | 'is_asset'
  | 'gemahnt'
  | 'extraction_attempts'
  | 'datev'
  | 'warnungen'
> = {
  currency: 'EUR',
  hat_rohmail: true,
  ist_auslage: false,
  is_asset: false,
  gemahnt: false,
  extraction_attempts: 1,
  datev: [],
  warnungen: [],
};

const BELEGE: DocumentResponse[] = [
  {
    ...GRUNDGERUEST,
    id: '11111111-1111-4111-8111-111111111111',
    original_name: 'Telekom_Rechnung_2026-08.pdf',
    mime_type: 'application/pdf',
    size_bytes: 184_320,
    sha256: 'a3f1'.repeat(16),
    source: 'sammelpostfach',
    source_mailbox: 'buchhaltung@elbklar.com',
    sender_address: 'rechnung@telekom.de',
    sender_auth: 'pass',
    art: 'rechnung',
    richtung: 'eingang',
    status: 'uebergeben',
    warnungen: [],
    supplier_id: 'aaaaaaaa-0000-4000-8000-000000000001',
    supplier_name: 'Telekom Deutschland GmbH',
    invoice_number: 'RE-2026-0884213',
    invoice_date: tage(-9),
    due_date: tage(5),
    service_period: '08/2026',
    leistung: 'Business-Anschluss und Mobilfunk',
    amount_gross: '218.41',
    amount_net: '183.54',
    vat_rate: '19',
    vat_amount: '34.87',
    vat_lines: [
      { satz: '19', netto: '148.74', steuer: '28.26' },
      { satz: '7', netto: '34.80', steuer: '2.44' },
    ],
    iban: 'DE12500700100123456789',
    ibans_im_text: ['DE12500700100123456789'],
    aussteller_ust_id: 'DE123475223',
    aussteller_anschrift: 'Landgrabenweg 151, 53227 Bonn',
    empfaenger_ok: true,
    steuer_hinweis: 'regel',
    archive_name: `${tage(-9)}_Telekom_218-41_RE-2026-0884213.pdf`,
    datev: [
      {
        id: 'dddddddd-0000-4000-8000-000000000001',
        attempt: 1,
        recipient: 'henri.salzer@elbklar.com',
        mode: 'trocken',
        status: 'uebergeben_probe',
        message_id: '<CAF9m@mail.gmail.com>',
        archive_name: `${tage(-9)}_Telekom_218-41_RE-2026-0884213.pdf`,
        sent_at: stunden(-200),
      },
    ],
    extraction: { model: 'claude', prompt_version: '1', schema_version: '1' },
    extracted_at: stunden(-201),
    created_at: stunden(-202),
    updated_at: stunden(-200),
  },
  {
    ...GRUNDGERUEST,
    id: '22222222-2222-4222-8222-222222222222',
    original_name: 'Rechnung_4711.pdf',
    mime_type: 'application/pdf',
    size_bytes: 96_140,
    sha256: 'b7c2'.repeat(16),
    source: 'sammelpostfach',
    source_mailbox: 'buchhaltung@elbklar.com',
    sender_address: 'buchhaltung@musterbau-gmbh.de',
    sender_auth: 'pass',
    art: 'rechnung',
    richtung: 'eingang',
    status: 'pruefung',
    // Der Fall, um den es bei der ganzen Echtheitspruefung geht: bekannter
    // Lieferant, neue IBAN. Rot, und die Oberflaeche gibt Rot nicht frei.
    warnungen: [
      {
        schluessel: 'iban_geaendert',
        stufe: 'rot',
        text: 'Die IBAN auf dem Beleg ist eine andere als die bekannte. Vor dem Zahlen anrufen — unter der Nummer, die ihr schon habt, nicht der aus der Mail.',
        belege: {
          gefunden: 'DE02120300000000202051',
          bekannt: 'DE89370400440532013000',
        },
      },
      {
        schluessel: 'betrag_ungewoehnlich',
        stufe: 'gelb',
        text: 'Der Betrag liegt deutlich über den letzten fünf Rechnungen dieses Lieferanten (Median 1.240,00 €).',
        belege: { median: '1240.00', aktuell: '8940.00' },
      },
    ],
    supplier_id: 'aaaaaaaa-0000-4000-8000-000000000002',
    supplier_name: 'Musterbau GmbH',
    invoice_number: '4711',
    invoice_date: tage(-2),
    due_date: tage(12),
    leistung: 'Gerüststellung Bauvorhaben Coswig',
    amount_gross: '8940.00',
    amount_net: '7512.61',
    vat_rate: '19',
    vat_amount: '1427.39',
    iban: 'DE02120300000000202051',
    ibans_im_text: ['DE02120300000000202051'],
    aussteller_ust_id: 'DE811907980',
    aussteller_anschrift: 'Industriestraße 4, 01640 Coswig',
    empfaenger_ok: true,
    steuer_hinweis: 'regel',
    extraction: { model: 'claude', prompt_version: '1', schema_version: '1' },
    extracted_at: stunden(-30),
    created_at: stunden(-31),
    updated_at: stunden(-30),
  },
  {
    ...GRUNDGERUEST,
    id: '33333333-3333-4333-8333-333333333333',
    original_name: 'IMG_4821.jpeg',
    mime_type: 'image/jpeg',
    size_bytes: 1_842_000,
    sha256: 'c9d3'.repeat(16),
    source: 'kurzbefehl',
    sender_address: 'henri.salzer@elbklar.com',
    sender_auth: 'pass',
    art: 'rechnung',
    richtung: 'eingang',
    status: 'freigegeben',
    warnungen: [
      {
        schluessel: 'aussteller_neu',
        stufe: 'gelb',
        text: 'Diesen Aussteller gab es noch nie. Die erste Rechnung eines neuen Lieferanten wird bewusst freigegeben.',
        belege: { name: 'Autohof Nossen' },
      },
      {
        schluessel: 'iban_nur_bild',
        stufe: 'gelb',
        text: 'Der Beleg hat keine Textebene — die IBAN ließ sich nicht deterministisch prüfen, nur lesen.',
      },
    ],
    supplier_name: 'Autohof Nossen',
    invoice_number: 'B-88213',
    invoice_date: tage(-1),
    leistung: 'Diesel, 62,4 l',
    amount_gross: '104.32',
    amount_net: '87.66',
    vat_rate: '19',
    vat_amount: '16.66',
    ibans_im_text: null,
    empfaenger_ok: false,
    steuer_hinweis: 'kleinbetrag',
    ist_auslage: true,
    auslage_von: 'Henri Salzer',
    hat_rohmail: false,
    extraction: { model: 'claude', prompt_version: '1', schema_version: '1' },
    extracted_at: stunden(-20),
    created_at: stunden(-21),
    updated_at: stunden(-20),
  },
  {
    ...GRUNDGERUEST,
    id: '44444444-4444-4444-8444-444444444444',
    original_name: 'xrechnung.xml',
    mime_type: 'application/xml',
    size_bytes: 24_180,
    sha256: 'd4e5'.repeat(16),
    source: 'sammelpostfach',
    source_mailbox: 'buchhaltung@elbklar.com',
    sender_address: 'billing@hetzner.com',
    sender_auth: 'pass',
    art: 'rechnung',
    richtung: 'eingang',
    status: 'freigegeben',
    warnungen: [],
    supplier_id: 'aaaaaaaa-0000-4000-8000-000000000003',
    supplier_name: 'Hetzner Online GmbH',
    invoice_number: 'R0012345678',
    invoice_date: tage(-4),
    due_date: tage(-1),
    service_period: '08/2026',
    leistung: 'Cloud-Server CX41, Storage Box BX11',
    amount_gross: '47.60',
    amount_net: '40.00',
    vat_rate: '19',
    vat_amount: '7.60',
    vat_lines: [{ satz: '19', netto: '40.00', steuer: '7.60' }],
    iban: 'DE39600501010002052500',
    // Bei einer XRechnung kommt die IBAN aus dem XML — deterministisch, ohne
    // Modell. Deshalb ist die Liste hier gefuellt und nicht `null`.
    ibans_im_text: ['DE39600501010002052500'],
    aussteller_ust_id: 'DE812871812',
    aussteller_anschrift: 'Industriestr. 25, 91710 Gunzenhausen',
    empfaenger_ok: true,
    steuer_hinweis: 'regel',
    archive_name: `${tage(-4)}_Hetzner_47-60_R0012345678.pdf`,
    // Die Feldtabelle der XML-Vorschau liest genau das hier aus.
    extraction: {
      model: 'xml',
      prompt_version: '—',
      schema_version: 'CII D16B',
      felder: {
        'BT-1 Rechnungsnummer': 'R0012345678',
        'BT-2 Rechnungsdatum': tage(-4),
        'BT-5 Währung': 'EUR',
        'BT-9 Fälligkeitsdatum': tage(-1),
        'BT-27 Verkäufer': 'Hetzner Online GmbH',
        'BT-31 USt-Id Verkäufer': 'DE812871812',
        'BT-44 Käufer': 'Elbklar GmbH',
        'BT-84 IBAN': 'DE39600501010002052500',
        'BT-106 Summe netto': '40.00',
        'BT-110 Steuerbetrag': '7.60',
        'BT-112 Summe brutto': '47.60',
        'BT-115 Zahlbetrag': '47.60',
      },
    },
    extracted_at: stunden(-90),
    created_at: stunden(-91),
    updated_at: stunden(-88),
  },
  {
    ...GRUNDGERUEST,
    id: '55555555-5555-4555-8555-555555555555',
    original_name: 'Mahnung_2026-09.pdf',
    mime_type: 'application/pdf',
    size_bytes: 61_200,
    sha256: 'e5f6'.repeat(16),
    source: 'sammelpostfach',
    source_mailbox: 'buchhaltung@elbklar.com',
    sender_address: 'mahnwesen@buerobedarf-nord.de',
    sender_auth: 'pass',
    art: 'mahnung',
    richtung: 'eingang',
    status: 'freigegeben',
    gemahnt: true,
    warnungen: [
      {
        schluessel: 'rechnung_fehlt',
        stufe: 'gelb',
        text: 'Zu dieser Mahnung gibt es keine Rechnung im Bestand. Die Originalrechnung ist nie angekommen — beim Lieferanten anfordern.',
        belege: { gesucht: 'RE-90218' },
      },
    ],
    supplier_name: 'Bürobedarf Nord KG',
    invoice_number: 'M-2026-114',
    bezug_rechnungsnummer: 'RE-90218',
    mahngebuehr: '5.00',
    invoice_date: tage(-3),
    due_date: tage(-6),
    amount_gross: '312.90',
    amount_net: '262.94',
    vat_rate: '19',
    vat_amount: '49.96',
    ibans_im_text: ['DE44500105175407324931'],
    iban: 'DE44500105175407324931',
    empfaenger_ok: true,
    steuer_hinweis: 'regel',
    extraction: { model: 'claude', prompt_version: '1', schema_version: '1' },
    extracted_at: stunden(-52),
    created_at: stunden(-53),
    updated_at: stunden(-52),
  },
  {
    ...GRUNDGERUEST,
    id: '66666666-6666-4666-8666-666666666666',
    original_name: 'Newsletter_September.pdf',
    mime_type: 'application/pdf',
    size_bytes: 2_410_000,
    sha256: 'f7a8'.repeat(16),
    source: 'sammelpostfach',
    source_mailbox: 'buchhaltung@elbklar.com',
    sender_address: 'news@baustoff-magazin.de',
    sender_auth: 'fail',
    art: 'sonstiges',
    richtung: 'eingang',
    status: 'neu',
    warnungen: [
      {
        schluessel: 'absender_fremd',
        stufe: 'gelb',
        text: 'Die Echtheitsprüfung des Absenders ist fehlgeschlagen (DKIM fail) und die Domain steht nicht in der Lieferantenliste.',
        belege: { domain: 'baustoff-magazin.de', sender_auth: 'fail' },
      },
    ],
    ibans_im_text: [],
    extraction_error: 'Kein Beleg erkannt — weder Aussteller noch Betrag gefunden.',
    extraction_attempts: 3,
    created_at: stunden(-6),
    updated_at: stunden(-5),
  },
];

const LOGS: Record<string, LogEintrag[]> = {
  '22222222-2222-4222-8222-222222222222': [
    {
      id: 'l1',
      step: 'angenommen',
      actor: 'system',
      detail: { sha256: 'b7c2…', quelle: 'sammelpostfach', groesse: 96140 },
      created_at: stunden(-31),
    },
    {
      id: 'l2',
      step: 'abgelegt',
      actor: 'system',
      detail: { pfad: 'belege/2026/09/…' },
      created_at: stunden(-31),
    },
    {
      id: 'l3',
      step: 'ausgelesen',
      actor: 'system',
      detail: { model: 'claude', dauer_s: 4.2 },
      created_at: stunden(-30),
    },
    {
      id: 'l4',
      step: 'geprueft',
      actor: 'system',
      detail: { warnungen: ['iban_geaendert', 'betrag_ungewoehnlich'] },
      created_at: stunden(-30),
    },
  ],
};

function standardLog(beleg: DocumentResponse): LogEintrag[] {
  return [
    {
      id: `${beleg.id}-1`,
      step: 'angenommen',
      actor: 'system',
      detail: { quelle: beleg.source, groesse: beleg.size_bytes },
      created_at: beleg.created_at,
    },
    {
      id: `${beleg.id}-2`,
      step: beleg.extraction_error ? 'auslesen_fehlgeschlagen' : 'ausgelesen',
      actor: 'system',
      detail: beleg.extraction_error
        ? { fehler: beleg.extraction_error, versuche: beleg.extraction_attempts }
        : { model: 'claude' },
      created_at: beleg.updated_at,
    },
  ];
}

// ---------------------------------------------------------------------------
// Bank, Einstellungen, Health
// ---------------------------------------------------------------------------

const KONTO: BankAccountResponse = {
  id: 'bbbbbbbb-0000-4000-8000-000000000001',
  iban: 'DE21850550003100123456',
  bic: 'SOLADES1MEI',
  name: 'Elbklar GmbH — Geschäftskonto',
  ebics_user_id: 'ELBK01',
  last_fetch_at: stunden(-3),
  last_statement_date: tage(-1),
  tage_ohne_auszug: 1,
};

const UMSAETZE: BankTransactionResponse[] = [
  {
    id: 'cccccccc-0000-4000-8000-000000000001',
    account_id: KONTO.id,
    booking_date: tage(-1),
    value_date: tage(-1),
    amount: '-47.60',
    counterparty_name: 'Hetzner Online GmbH',
    counterparty_iban: 'DE39600501010002052500',
    purpose: 'R0012345678 Kundennr 4711223',
    end_to_end_id: 'NOTPROVIDED',
    match_status: 'vorschlag',
    vorschlaege: [
      {
        document_id: '44444444-4444-4444-8444-444444444444',
        bezeichnung: 'Hetzner Online GmbH · R0012345678 · 47,60 €',
        grund: 'betrag_iban',
        zuversicht: 'hoch',
      },
      {
        document_id: '11111111-1111-4111-8111-111111111111',
        bezeichnung: 'Telekom Deutschland GmbH · RE-2026-0884213 · 218,41 €',
        grund: 'verwendungszweck',
        zuversicht: 'niedrig',
      },
    ],
  },
  {
    id: 'cccccccc-0000-4000-8000-000000000002',
    account_id: KONTO.id,
    booking_date: tage(-2),
    value_date: tage(-2),
    amount: '-1250.00',
    counterparty_name: 'ANTON WEBER',
    counterparty_iban: 'DE68210501700012345678',
    purpose: 'Erstattung Auslage B-88213',
    mandate_reference: null,
    match_status: 'offen',
    vorschlaege: [],
  },
  {
    id: 'cccccccc-0000-4000-8000-000000000003',
    account_id: KONTO.id,
    booking_date: tage(-4),
    amount: '-218.41',
    counterparty_name: 'Telekom Deutschland GmbH',
    counterparty_iban: null,
    purpose: 'Lastschrift Kundenkonto 884213',
    mandate_reference: 'M-884213',
    match_status: 'zugeordnet',
    document_id: '11111111-1111-4111-8111-111111111111',
    document_bezeichnung: 'Telekom Deutschland GmbH · RE-2026-0884213',
    vorschlaege: [],
  },
];

const EINSTELLUNGEN: BuchhaltungEinstellungen = {
  datev: {
    upload_eingang: 'rechnungseingang-elbklar@datevnet.de',
    upload_ausgang: 'rechnungsausgang-elbklar@datevnet.de',
    modus: 'trocken',
    probe_empfaenger: 'henri.salzer@elbklar.com',
    kanzlei_domain: 'ruehle-steuerberatung.de',
  },
  postfaecher: {
    sammelpostfach: 'buchhaltung@elbklar.com',
    sweeper_postfaecher: ['henri.salzer@elbklar.com', 'anton.weber@elbklar.com'],
    lese_service_account_gesetzt: true,
  },
  ebics: {
    eingerichtet: true,
    host_id: 'SOLADEST',
    partner_id: 'ELBKLAR1',
    user_id: 'ELBK01',
    lizenz_gesetzt: true,
    letzter_abruf_at: stunden(-3),
    letzter_auszug_am: tage(-1),
  },
  zahlende_user_ids: ['Henri Salzer', 'Anton Weber'],
  barbeleg_schwelle_eur: 150,
  auslesen_eingerichtet: true,
  max_auslese_je_tag: 200,
  auslese_heute: 14,
};

const HEALTH: IngestHealth = {
  ok: true,
  ingest_alter_stunden: 0.3,
  quellen: [
    {
      quelle: 'sammelpostfach',
      letzter_lauf_at: stunden(-0.3),
      letzter_erfolg_at: stunden(-0.3),
      alter_stunden: 0.3,
      fehllaeufe_in_folge: 0,
      ok: true,
    },
    {
      quelle: 'ebics',
      letzter_lauf_at: stunden(-3),
      letzter_erfolg_at: stunden(-3),
      alter_stunden: 3,
      fehllaeufe_in_folge: 0,
      ok: true,
    },
    {
      quelle: 'sweeper',
      letzter_lauf_at: stunden(-14),
      letzter_erfolg_at: stunden(-14),
      alter_stunden: 14,
      fehllaeufe_in_folge: 0,
      ok: true,
    },
  ],
};

/**
 * Die Pruefliste — Funde des Sweepers in den **persoenlichen** Postfaechern.
 *
 * Zwei Postfaecher mit Absicht: Nur so laesst sich in der Klickprobe sehen,
 * dass ein Mitarbeitender die Zeile des anderen gar nicht erst zu Gesicht
 * bekommt (`lib/rechte.ts`).
 */
const PRUEFLISTE: ReviewItemResponse[] = [
  {
    id: 'dddddddd-0000-4000-8000-000000000001',
    mailbox: 'henri.salzer@elbklar.com',
    message_id: '<CAF1a9@mail.gmail.com>',
    subject: 'Ihre Rechnung 2026-0912 zur Weiterbildung',
    sender_address: 'rechnung@bildungswerk-sachsen.de',
    decision: 'offen',
    created_at: stunden(-5),
    original_name: 'Rechnung_2026-0912.pdf',
    mime_type: 'application/pdf',
    amount_hinweis: '480,00 €',
  },
  {
    id: 'dddddddd-0000-4000-8000-000000000002',
    mailbox: 'henri.salzer@elbklar.com',
    message_id: '<CAF2b7@mail.gmail.com>',
    subject: 'Newsletter September — Neues aus der Kammer',
    sender_address: 'news@hwk-dresden.de',
    decision: 'offen',
    created_at: stunden(-9),
    original_name: 'Kammerbrief_09.pdf',
    mime_type: 'application/pdf',
  },
  {
    id: 'dddddddd-0000-4000-8000-000000000003',
    mailbox: 'anton.weber@elbklar.com',
    message_id: '<CAF3c1@mail.gmail.com>',
    subject: 'Tankquittung vom Wochenende',
    sender_address: 'anton.weber@elbklar.com',
    decision: 'offen',
    created_at: stunden(-26),
    original_name: 'IMG_5510.jpeg',
    mime_type: 'image/jpeg',
    amount_hinweis: '92,40 €',
  },
  {
    id: 'dddddddd-0000-4000-8000-000000000004',
    mailbox: 'henri.salzer@elbklar.com',
    message_id: '<CAF4d5@mail.gmail.com>',
    subject: 'Rechnung Hetzner August',
    sender_address: 'billing@hetzner.com',
    decision: 'uebernommen',
    decided_by: 'u-1',
    decided_at: tage(-2),
    document_id: '44444444-4444-4444-8444-444444444444',
    created_at: tage(-2),
    original_name: 'R0012345678.pdf',
    mime_type: 'application/pdf',
  },
];

/**
 * Lieferanten mit ihren IBANs.
 *
 * „Musterbau GmbH" ist der Fall aus dem Beleg mit der roten Warnung: die
 * bekannte IBAN ist bestaetigt, die vom Beleg steht noch nicht drin. Genau
 * dieses Paar macht die Freigabe mit Rueckruf ueberhaupt anschaulich.
 */
const LIEFERANTEN: SupplierResponse[] = [
  {
    id: 'aaaaaaaa-0000-4000-8000-000000000001',
    name: 'Telekom Deutschland GmbH',
    aliases: ['Telekom', 'Deutsche Telekom AG'],
    mail_domains: ['telekom.de', 'telekom-rechnung.de'],
    expected_interval: 'monatlich',
    customer_number: '0491234567',
    notes: null,
    ibans: [
      {
        id: 'bbbbbbbb-0000-4000-8000-000000000001',
        iban: 'DE12500700100123456789',
        confirmed_by: 'u-1',
        confirmed_by_name: 'Henri Salzer',
        confirmed_at: tage(-96),
      },
    ],
    belege_gesamt: 14,
    created_at: tage(-96),
  },
  {
    id: 'aaaaaaaa-0000-4000-8000-000000000002',
    name: 'Musterbau GmbH',
    aliases: ['Musterbau'],
    mail_domains: ['musterbau-gmbh.de'],
    expected_interval: 'unregelmäßig',
    customer_number: 'EK-2026-04',
    notes: 'Gerüstbau. Rückrufnummer aus dem Vertrag, nicht aus der Mail.',
    ibans: [
      {
        id: 'bbbbbbbb-0000-4000-8000-000000000002',
        iban: 'DE89370400440532013000',
        confirmed_by: 'u-1',
        confirmed_by_name: 'Henri Salzer',
        confirmed_at: tage(-61),
      },
      {
        id: 'bbbbbbbb-0000-4000-8000-000000000003',
        iban: 'DE02120300000000202051',
        first_seen_document_id: '22222222-2222-4222-8222-222222222222',
        confirmed_at: null,
      },
    ],
    belege_gesamt: 4,
    created_at: tage(-61),
  },
  {
    id: 'aaaaaaaa-0000-4000-8000-000000000003',
    name: 'Hetzner Online GmbH',
    aliases: [],
    mail_domains: ['hetzner.com', 'hetzner.de'],
    expected_interval: 'monatlich',
    customer_number: 'K1234567',
    notes: null,
    ibans: [
      {
        id: 'bbbbbbbb-0000-4000-8000-000000000004',
        iban: 'DE39600501010002052500',
        confirmed_by: 'u-2',
        confirmed_by_name: 'Anton Weber',
        confirmed_at: tage(-40),
      },
    ],
    belege_gesamt: 9,
    created_at: tage(-40),
  },
  {
    id: 'aaaaaaaa-0000-4000-8000-000000000004',
    name: 'Autohof Nossen',
    aliases: [],
    mail_domains: [],
    expected_interval: null,
    customer_number: null,
    notes: 'Barbeleg, entstanden über „Freigeben".',
    ibans: [],
    belege_gesamt: 1,
    created_at: tage(-3),
  },
];

// ---------------------------------------------------------------------------
// Der Zustand dieser Sitzung
// ---------------------------------------------------------------------------

const zustand = {
  belege: BELEGE.map((b) => ({ ...b })),
  umsaetze: UMSAETZE.map((u) => ({ ...u })),
  pruefliste: PRUEFLISTE.map((p) => ({ ...p })),
  lieferanten: LIEFERANTEN.map((l) => ({ ...l, ibans: l.ibans.map((i) => ({ ...i })) })),
};

function verzoegern<T>(wert: T, ms = 180): Promise<T> {
  // Ein wenig Latenz, damit Ladezustaende und Spinner ueberhaupt sichtbar sind
  // — ein Mock, der sofort antwortet, verdeckt genau die Zustaende, die im
  // Betrieb dann haengen.
  return new Promise((auf) => setTimeout(() => auf(wert), ms));
}

function findeBeleg(id: string): DocumentResponse {
  const beleg = zustand.belege.find((b) => b.id === id);
  if (!beleg) throw new Error(`Beleg ${id} gibt es im Mock nicht`);
  return beleg;
}

function alsZeile(beleg: DocumentResponse): DocumentListItem {
  return {
    id: beleg.id,
    original_name: beleg.original_name,
    art: beleg.art,
    richtung: beleg.richtung,
    status: beleg.status,
    supplier_name: beleg.supplier_name ?? null,
    invoice_number: beleg.invoice_number ?? null,
    invoice_date: beleg.invoice_date ?? null,
    due_date: beleg.due_date ?? null,
    amount_gross: beleg.amount_gross ?? null,
    currency: beleg.currency,
    warnungen_rot: beleg.warnungen.filter((w) => w.stufe === 'rot').length,
    warnungen_gelb: beleg.warnungen.filter((w) => w.stufe === 'gelb').length,
    gemahnt: beleg.gemahnt,
    paid_at: beleg.paid_at ?? null,
    datev_status: beleg.datev[0]?.status ?? 'offen',
    created_at: beleg.created_at,
  };
}

export interface BelegFilter {
  status?: string[];
  art?: string[];
  nur_mit_warnungen?: boolean;
  von?: string;
  bis?: string;
  suche?: string;
  offset?: number;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Die Mock-Umsetzung der API
// ---------------------------------------------------------------------------

export const mockApi = {
  async belege(filter: BelegFilter = {}): Promise<DocumentListe> {
    let treffer = zustand.belege.map(alsZeile);

    if (filter.status?.length) {
      treffer = treffer.filter((z) => filter.status!.includes(z.status));
    }
    if (filter.art?.length) {
      treffer = treffer.filter((z) => filter.art!.includes(z.art));
    }
    if (filter.nur_mit_warnungen) {
      treffer = treffer.filter((z) => z.warnungen_rot + z.warnungen_gelb > 0);
    }
    if (filter.von) treffer = treffer.filter((z) => z.created_at.slice(0, 10) >= filter.von!);
    if (filter.bis) treffer = treffer.filter((z) => z.created_at.slice(0, 10) <= filter.bis!);
    if (filter.suche) {
      const suche = filter.suche.toLowerCase();
      treffer = treffer.filter((z) =>
        [z.supplier_name, z.invoice_number, z.original_name]
          .filter(Boolean)
          .some((feld) => feld!.toLowerCase().includes(suche)),
      );
    }

    // Neueste zuerst — wer den Eingang oeffnet, will wissen, was gerade kam.
    treffer.sort((a, b) => b.created_at.localeCompare(a.created_at));

    return verzoegern({
      items: treffer,
      total: treffer.length,
      offset: filter.offset ?? 0,
      limit: filter.limit ?? 50,
    });
  },

  async beleg(id: string): Promise<DocumentResponse> {
    return verzoegern({ ...findeBeleg(id) });
  },

  belegDateiUrl(id: string): string {
    const beleg = zustand.belege.find((b) => b.id === id);
    if (beleg && beleg.mime_type.startsWith('image/')) {
      // Ein gezeichneter Kassenbon: im Mock-Modus ist die Bildvorschau damit
      // echt und nicht nur ein grauer Kasten.
      return bildAttrappe(beleg.supplier_name ?? 'Beleg', beleg.amount_gross ?? '');
    }
    return `data:text/plain,Mock-Modus%3A%20keine%20Datei%20hinterlegt%20(${id})`;
  },

  async belegLog(id: string): Promise<LogEintrag[]> {
    return verzoegern(LOGS[id] ?? standardLog(findeBeleg(id)));
  },

  async belegKorrigieren(id: string, patch: DocumentPatch): Promise<DocumentResponse> {
    const beleg = findeBeleg(id);
    Object.assign(beleg, patch);
    beleg.updated_at = new Date().toISOString();
    return verzoegern({ ...beleg });
  },

  /**
   * Freigeben — und bei `iban_bestaetigen` die IBAN uebernehmen.
   *
   * Der Server rechnet die Warnungen danach neu; hier wird dasselbe von Hand
   * getan: Die IBAN-Warnungen fallen weg, weil die IBAN jetzt bekannt ist.
   * Ohne das saehe die Detailseite nach der Freigabe genauso aus wie davor —
   * und die Klickprobe koennte nicht zeigen, dass der Rueckruf etwas bewirkt
   * hat.
   */
  async belegFreigeben(id: string, freigabe: Freigabe): Promise<DocumentResponse> {
    const beleg = findeBeleg(id);
    beleg.status = 'freigegeben';

    if (freigabe.iban_bestaetigen && beleg.iban) {
      beleg.warnungen = beleg.warnungen.filter(
        (w) =>
          w.schluessel !== 'iban_geaendert' &&
          w.schluessel !== 'iban_unbestaetigt' &&
          w.schluessel !== 'iban_nur_bild',
      );
      const lieferant = zustand.lieferanten.find((l) => l.id === beleg.supplier_id);
      const vorhanden = lieferant?.ibans.find((i) => i.iban === beleg.iban);
      if (vorhanden) {
        vorhanden.confirmed_by = 'u-1';
        vorhanden.confirmed_by_name = 'Henri Salzer';
        vorhanden.confirmed_at = new Date().toISOString();
      } else if (lieferant) {
        lieferant.ibans = [
          ...lieferant.ibans,
          {
            id: `iban-${Math.random().toString(36).slice(2, 8)}`,
            iban: beleg.iban,
            first_seen_document_id: beleg.id,
            confirmed_by: 'u-1',
            confirmed_by_name: 'Henri Salzer',
            confirmed_at: new Date().toISOString(),
          },
        ];
      }
    }

    beleg.updated_at = new Date().toISOString();
    return verzoegern({ ...beleg });
  },

  async belegAblehnen(id: string, ablehnung: Ablehnung): Promise<DocumentResponse> {
    const beleg = findeBeleg(id);
    beleg.status = 'abgelehnt';
    beleg.rejected_reason = ablehnung.reason;
    beleg.updated_at = new Date().toISOString();
    return verzoegern({ ...beleg });
  },

  async belegBezahlt(id: string, zahlung: Zahlung): Promise<DocumentResponse> {
    const beleg = findeBeleg(id);
    beleg.paid_at = zahlung.datum;
    beleg.paid_amount = zahlung.betrag ?? beleg.amount_gross ?? null;
    beleg.updated_at = new Date().toISOString();
    return verzoegern({ ...beleg });
  },

  async belegErneutAuslesen(id: string): Promise<DocumentResponse> {
    const beleg = findeBeleg(id);
    beleg.extraction_attempts += 1;
    beleg.extracted_at = new Date().toISOString();
    beleg.extraction_error = null;
    beleg.updated_at = beleg.extracted_at;
    return verzoegern({ ...beleg }, 700);
  },

  async belegErneutUebergeben(id: string): Promise<DocumentResponse> {
    const beleg = findeBeleg(id);
    beleg.datev = [
      {
        id: `neu-${beleg.datev.length + 1}`,
        attempt: beleg.datev.length + 1,
        recipient: EINSTELLUNGEN.datev.probe_empfaenger ?? 'henri.salzer@elbklar.com',
        mode: EINSTELLUNGEN.datev.modus,
        status: EINSTELLUNGEN.datev.modus === 'echt' ? 'uebergeben' : 'uebergeben_probe',
        archive_name: beleg.archive_name ?? null,
        sent_at: new Date().toISOString(),
      },
      ...beleg.datev,
    ];
    beleg.status = 'uebergeben';
    beleg.updated_at = new Date().toISOString();
    return verzoegern({ ...beleg }, 700);
  },

  async belegIstBeleg(id: string, art: Belegart): Promise<DocumentResponse> {
    const beleg = findeBeleg(id);
    beleg.art = art;
    beleg.status = 'pruefung';
    beleg.updated_at = new Date().toISOString();
    return verzoegern({ ...beleg });
  },

  async erinnerungAus(_id: string): Promise<Bestaetigung> {
    return verzoegern({ ok: true, hinweis: 'Erinnerung abgestellt.' });
  },

  async hochladen(
    dateien: File[],
    _optionen: { art?: Belegart; notiz?: string } = {},
    beiFortschritt?: (prozent: number) => void,
  ): Promise<UploadErgebnis> {
    // Fortschritt vortaeuschen, damit der Balken auf `/scan` im Mock nicht
    // von 0 auf fertig springt und man ihn also nie sieht.
    for (const schritt of [15, 45, 75, 100]) {
      await verzoegern(null, 160);
      beiFortschritt?.(schritt);
    }

    const jetzt = new Date().toISOString();
    const neu: DocumentResponse = {
      ...GRUNDGERUEST,
      id: `neu-${Math.random().toString(36).slice(2, 10)}`,
      original_name: dateien.length > 1 ? `Scan_${dateien.length}_Seiten.pdf` : dateien[0].name,
      mime_type: dateien.length > 1 ? 'application/pdf' : dateien[0].type,
      size_bytes: dateien.reduce((summe, d) => summe + d.size, 0),
      sha256: Math.random().toString(16).slice(2).padEnd(64, '0'),
      hat_rohmail: false,
      source: 'scan',
      sender_auth: 'pass',
      art: 'unklar',
      richtung: 'eingang',
      status: 'neu',
      warnungen: [],
      ibans_im_text: null,
      extraction_attempts: 0,
      datev: [],
      created_at: jetzt,
      updated_at: jetzt,
    };
    zustand.belege.unshift(neu);

    return { document: neu, seiten: dateien.length, duplikat_von: null };
  },

  async zuZahlen(nurFaellig = false): Promise<ZuZahlenListe> {
    const heute = new Date();
    const eintraege = zustand.belege
      .filter(
        (b) =>
          (b.art === 'rechnung' || b.art === 'mahnung') &&
          ['freigegeben', 'uebergeben'].includes(b.status) &&
          !b.paid_at,
      )
      .map((b) => {
        const faellig = b.due_date ? new Date(b.due_date) : null;
        const tage_bis_faellig = faellig
          ? Math.round((faellig.getTime() - heute.getTime()) / 86_400_000)
          : null;
        // Nur bestaetigte IBANs erscheinen. Bei „Autohof Nossen" (neu) gibt es
        // keine — die Liste sagt das, statt die vom Beleg zu zeigen.
        const bestaetigt = ['Telekom Deutschland GmbH', 'Hetzner Online GmbH'].includes(
          b.supplier_name ?? '',
        );
        return {
          id: b.id,
          supplier_name: b.supplier_name ?? null,
          invoice_number: b.invoice_number ?? null,
          invoice_date: b.invoice_date ?? null,
          due_date: b.due_date ?? null,
          amount_gross: b.amount_gross ?? null,
          currency: b.currency,
          gemahnt: b.gemahnt,
          tage_bis_faellig,
          iban: bestaetigt ? (b.iban ?? null) : null,
          iban_stand: (bestaetigt
            ? 'bestaetigt'
            : b.iban
              ? 'unbestaetigt'
              : 'keine') as ZuZahlenListe['items'][number]['iban_stand'],
          ist_auslage: b.ist_auslage,
          auslage_von: b.auslage_von ?? null,
          warnungen_rot: b.warnungen.filter((w) => w.stufe === 'rot').length,
          warnungen_gelb: b.warnungen.filter((w) => w.stufe === 'gelb').length,
        };
      })
      .filter((e) => !nurFaellig || (e.tage_bis_faellig ?? 99) <= 3);

    // Gemahntes ganz nach oben, danach nach Faelligkeit.
    eintraege.sort((a, b) => {
      if (a.gemahnt !== b.gemahnt) return a.gemahnt ? -1 : 1;
      return (a.tage_bis_faellig ?? 999) - (b.tage_bis_faellig ?? 999);
    });

    const summe = eintraege.reduce((s, e) => s + Number(e.amount_gross ?? 0), 0);
    return verzoegern({
      items: eintraege,
      summe_offen: summe.toFixed(2),
      anzahl_gemahnt: eintraege.filter((e) => e.gemahnt).length,
    });
  },

  async bankKonten(): Promise<BankAccountResponse[]> {
    return verzoegern([KONTO]);
  },

  async bankUmsaetze(nurOffen = true): Promise<BankListe> {
    const items = nurOffen
      ? zustand.umsaetze.filter((u) => u.match_status === 'offen' || u.match_status === 'vorschlag')
      : zustand.umsaetze;
    return verzoegern({
      items: items.map((u) => ({ ...u })),
      total: items.length,
      offen: zustand.umsaetze.filter((u) => u.match_status === 'offen').length,
      mehrdeutig: zustand.umsaetze.filter((u) => u.vorschlaege.length > 1).length,
    });
  },

  async bankZuordnen(
    id: string,
    zuordnung: ZuordnungBestaetigen,
  ): Promise<BankTransactionResponse> {
    const umsatz = zustand.umsaetze.find((u) => u.id === id);
    if (!umsatz) throw new Error(`Buchung ${id} gibt es im Mock nicht`);
    if (zuordnung.document_id) {
      umsatz.match_status = 'zugeordnet';
      umsatz.document_id = zuordnung.document_id;
      umsatz.document_bezeichnung =
        umsatz.vorschlaege.find((v) => v.document_id === zuordnung.document_id)?.bezeichnung ??
        'zugeordnet';
    } else {
      umsatz.match_status = 'ignoriert';
      umsatz.document_id = null;
    }
    umsatz.vorschlaege = [];
    return verzoegern({ ...umsatz });
  },

  async einstellungen(): Promise<BuchhaltungEinstellungen> {
    return verzoegern(EINSTELLUNGEN);
  },

  async ingestHealth(): Promise<IngestHealth> {
    return verzoegern(HEALTH);
  },

  // -------------------------------------------------------------------------
  // Prüfliste
  // -------------------------------------------------------------------------

  async pruefliste(nurOffen = true): Promise<ReviewListe> {
    // Der echte Server filtert zusaetzlich nach Postfach — das kann der Mock
    // nicht, er kennt den Angemeldeten nicht. Die Seite filtert ohnehin selbst
    // (`lib/rechte.ts`), und genau das laesst sich so auch vorfuehren.
    const alle = zustand.pruefliste;
    const items = nurOffen ? alle.filter((e) => e.decision === 'offen') : alle;
    return verzoegern({
      items: items.map((e) => ({ ...e })),
      total: items.length,
      offen: alle.filter((e) => e.decision === 'offen').length,
    });
  },

  async prueflisteEntscheiden(
    id: string,
    entscheidung: ReviewEntscheidung,
  ): Promise<ReviewItemResponse> {
    const eintrag = zustand.pruefliste.find((e) => e.id === id);
    if (!eintrag) throw new Error(`Fund ${id} gibt es im Mock nicht`);

    eintrag.decision = entscheidung.decision;
    eintrag.reason = entscheidung.reason ?? null;
    eintrag.decided_by = 'u-1';
    eintrag.decided_at = new Date().toISOString();

    if (entscheidung.decision === 'uebernommen') {
      // Der Haken legt einen Beleg an — und der Fund verlinkt darauf. Ohne den
      // Link waere „übernommen" eine Behauptung ohne Ziel.
      const jetzt = new Date().toISOString();
      const neu: DocumentResponse = {
        ...GRUNDGERUEST,
        id: `neu-${Math.random().toString(36).slice(2, 10)}`,
        original_name: eintrag.original_name ?? 'Anhang',
        mime_type: eintrag.mime_type ?? 'application/pdf',
        size_bytes: 84_000,
        sha256: Math.random().toString(16).slice(2).padEnd(64, '0'),
        hat_rohmail: true,
        source: 'sweeper',
        source_mailbox: eintrag.mailbox,
        sender_address: eintrag.sender_address ?? null,
        sender_auth: 'pass',
        art: 'unklar',
        richtung: 'eingang',
        status: 'neu',
        warnungen: [],
        ibans_im_text: null,
        extraction_attempts: 0,
        datev: [],
        created_at: jetzt,
        updated_at: jetzt,
      };
      zustand.belege.unshift(neu);
      eintrag.document_id = neu.id;
    }

    return verzoegern({ ...eintrag });
  },

  // -------------------------------------------------------------------------
  // Lieferanten
  // -------------------------------------------------------------------------

  async lieferanten(suche?: string): Promise<SupplierListe> {
    let items = zustand.lieferanten;
    if (suche) {
      const s = suche.toLowerCase();
      items = items.filter(
        (l) =>
          l.name.toLowerCase().includes(s) ||
          l.aliases.some((a) => a.toLowerCase().includes(s)) ||
          l.mail_domains.some((d) => d.toLowerCase().includes(s)),
      );
    }
    return verzoegern({ items: items.map((l) => ({ ...l })), total: items.length });
  },

  async lieferantAnlegen(daten: SupplierCreate): Promise<SupplierResponse> {
    const neu: SupplierResponse = {
      id: `neu-${Math.random().toString(36).slice(2, 10)}`,
      name: daten.name,
      aliases: daten.aliases,
      mail_domains: daten.mail_domains,
      expected_interval: daten.expected_interval ?? null,
      customer_number: daten.customer_number ?? null,
      notes: daten.notes ?? null,
      ibans: [],
      belege_gesamt: 0,
      created_at: new Date().toISOString(),
    };
    zustand.lieferanten.unshift(neu);
    return verzoegern({ ...neu });
  },

  async lieferantAendern(id: string, daten: SupplierUpdate): Promise<SupplierResponse> {
    const lieferant = zustand.lieferanten.find((l) => l.id === id);
    if (!lieferant) throw new Error(`Lieferant ${id} gibt es im Mock nicht`);
    Object.assign(lieferant, daten);
    return verzoegern({ ...lieferant });
  },

  async ibanBestaetigen(id: string, bestaetigung: IbanBestaetigung): Promise<SupplierResponse> {
    const lieferant = zustand.lieferanten.find((l) => l.id === id);
    if (!lieferant) throw new Error(`Lieferant ${id} gibt es im Mock nicht`);

    const sauber = bestaetigung.iban.replace(/\s+/g, '').toUpperCase();
    const jetzt = new Date().toISOString();
    const vorhanden = lieferant.ibans.find((i) => i.iban === sauber);

    if (vorhanden) {
      vorhanden.confirmed_by = 'u-1';
      vorhanden.confirmed_by_name = 'Henri Salzer';
      vorhanden.confirmed_at = jetzt;
    } else {
      lieferant.ibans = [
        ...lieferant.ibans,
        {
          id: `iban-${Math.random().toString(36).slice(2, 8)}`,
          iban: sauber,
          confirmed_by: 'u-1',
          confirmed_by_name: 'Henri Salzer',
          confirmed_at: jetzt,
        },
      ];
    }

    return verzoegern({ ...lieferant });
  },

  async lieferantenSeedImport(daten: SeedImport): Promise<SupplierListe> {
    if (daten.ueberschreiben) zustand.lieferanten.length = 0;

    for (const eintrag of daten.suppliers) {
      const vorhanden = zustand.lieferanten.find((l) => l.name === eintrag.name);
      if (vorhanden) {
        Object.assign(vorhanden, eintrag);
        continue;
      }
      zustand.lieferanten.push({
        id: `seed-${Math.random().toString(36).slice(2, 10)}`,
        name: eintrag.name,
        aliases: eintrag.aliases ?? [],
        mail_domains: eintrag.mail_domains ?? [],
        expected_interval: eintrag.expected_interval ?? null,
        customer_number: eintrag.customer_number ?? null,
        notes: eintrag.notes ?? null,
        ibans: [],
        belege_gesamt: 0,
        created_at: new Date().toISOString(),
      });
    }

    return verzoegern({
      items: zustand.lieferanten.map((l) => ({ ...l })),
      total: zustand.lieferanten.length,
    });
  },

  // -------------------------------------------------------------------------
  // Export
  // -------------------------------------------------------------------------

  async uebergabeliste(zeitraum: string): Promise<Uebergabeliste> {
    const zeilen = zustand.belege
      .filter((b) => b.datev.length > 0)
      .map((b) => ({
        document_id: b.id,
        archive_name: b.archive_name ?? null,
        supplier_name: b.supplier_name ?? null,
        invoice_number: b.invoice_number ?? null,
        invoice_date: b.invoice_date ?? null,
        amount_gross: b.amount_gross ?? null,
        uebergeben_at: b.datev[0].sent_at ?? null,
        message_id: b.datev[0].message_id ?? null,
        mode: b.datev[0].mode,
      }));

    const summe = zeilen.reduce((s, z) => s + Number(z.amount_gross ?? 0), 0);
    return verzoegern({
      zeitraum,
      zeilen,
      anzahl: zeilen.length,
      summe_brutto: summe.toFixed(2),
    });
  },

  dokumentExportUrl(zeitraum: string): string {
    return `data:text/plain,Mock-Modus%3A%20hier%20laege%20das%20ZIP%20fuer%20${zeitraum}`;
  },
};

/**
 * Ein gezeichneter Kassenbon als `data:`-Adresse.
 *
 * Klein genug fuer eine URL, echt genug fuer eine Klickprobe: die Bildvorschau
 * zeigt im Mock etwas, das aussieht wie ein Beleg, statt eines leeren Rahmens,
 * den man von einem Fehler nicht unterscheiden koennte.
 */
function bildAttrappe(aussteller: string, betrag: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="380" height="520" viewBox="0 0 380 520">
    <rect width="380" height="520" fill="#fdfdfb"/>
    <rect x="18" y="18" width="344" height="484" fill="#ffffff" stroke="#e2e8f0"/>
    <text x="40" y="70" font-family="monospace" font-size="19" fill="#0f172a">${aussteller}</text>
    <text x="40" y="98" font-family="monospace" font-size="12" fill="#64748b">Mock-Modus — keine echte Datei</text>
    <line x1="40" y1="120" x2="340" y2="120" stroke="#cbd5e1"/>
    <text x="40" y="160" font-family="monospace" font-size="13" fill="#334155">Diesel 62,4 l</text>
    <text x="40" y="188" font-family="monospace" font-size="13" fill="#334155">MwSt 19 %</text>
    <line x1="40" y1="215" x2="340" y2="215" stroke="#cbd5e1"/>
    <text x="40" y="250" font-family="monospace" font-size="22" fill="#0f172a">${betrag} EUR</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
